import type { ReactNode } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { Tabs } from "antd"

export type PageHubTab = { key: string; label: ReactNode; path: string }

/**
 * Section hub: URL-synced tabs under the top bar, content below fills the page.
 * Access filtering and redirects stay with the caller (see Optimization.tsx).
 * Renders the matched child route when `children` is omitted.
 */
export const PageHub = ({
  tabs,
  activeKey,
  children,
}: {
  tabs: PageHubTab[]
  activeKey: string
  children?: ReactNode
}) => {
  const navigate = useNavigate()

  return (
    <div className="page-fill" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Tabs
        className="page-hub-tabs"
        activeKey={activeKey}
        onChange={key => {
          const tab = tabs.find(t => t.key === key)
          if (tab) navigate(tab.path)
        }}
        items={tabs.map(t => ({ key: t.key, label: t.label }))}
      />
      <div className="page-hub-body">
        {children ?? <Outlet />}
      </div>
    </div>
  )
}

/** Active tab = the tab whose path is the longest prefix of the current URL. */
export const activeHubTabKey = (tabs: PageHubTab[], pathname: string): string => {
  const path = pathname.replace(/\/+$/, "") || "/"
  let best: PageHubTab | undefined
  for (const t of tabs) {
    if ((path === t.path || path.startsWith(`${t.path}/`)) && (!best || t.path.length > best.path.length)) best = t
  }
  return (best ?? tabs[0]).key
}

/** PageHub for sections where every tab shares one access key (route guards stay in App.tsx). */
export const RoutedPageHub = ({ tabs }: { tabs: PageHubTab[] }) => {
  const location = useLocation()
  return <PageHub tabs={tabs} activeKey={activeHubTabKey(tabs, location.pathname)} />
}
